// Central place for everything the marketing pages need to describe themselves
// to search engines, social unfurlers and AI answer engines.
//
// Only ONE thing here is environment-specific: SITE_URL. Change it in this one
// spot to re-point every canonical, og:url, sitemap entry and JSON-LD @id.

/** Production origin. No trailing slash. Swap this if the domain changes. */
export const SITE_URL = 'https://reeve.hr';

/** Turn a root-relative path ("/", "/waitlist", "/og-image.png") into an absolute URL. */
export function absUrl(path: string): string {
  return new URL(path, SITE_URL).toString();
}

// Stable @id anchors so the schema.org nodes can reference one another instead
// of repeating themselves (Organization is defined once, pointed at everywhere).
const ORG_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;
const SOFTWARE_ID = `${SITE_URL}/#software`;

/** England, Scotland and Wales — deliberately NOT the whole UK (Northern Ireland is out of scope). */
const AREA_SERVED = [
  { '@type': 'AdministrativeArea', name: 'England' },
  { '@type': 'AdministrativeArea', name: 'Scotland' },
  { '@type': 'AdministrativeArea', name: 'Wales' },
];

// "Checklist" is deliberately kept in the waitlist page's meta description (see WaitlistPage), which
// is where it still earns its place as a search term. The product's own copy says "requirements",
// because a checklist sounds like a generic to-do list rather than what the law requires of you.
const PRODUCT_DESCRIPTION =
  'reeve is an AI-powered retained HR copilot for UK small businesses with no HR function. ' +
  'It knows current UK employment law, checks your own HR documents and keeps a living list of ' +
  'the ones your business needs, and escalates to CIPD-qualified human experts when you need one.';

/** The brand/company entity. Referenced as publisher/provider by the other nodes. */
export const organizationSchema = {
  '@type': 'Organization',
  '@id': ORG_ID,
  name: 'reeve',
  url: `${SITE_URL}/`,
  logo: absUrl('/logo-512.png'),
  description: PRODUCT_DESCRIPTION,
  areaServed: AREA_SERVED,
  parentOrganization: { '@type': 'Organization', name: 'Arcology' },
  // Add LinkedIn / X / Companies House URLs here as they go live to strengthen
  // the entity in the Knowledge Graph:
  // sameAs: ['https://www.linkedin.com/company/...'],
};

/** The site entity. */
export const websiteSchema = {
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  name: 'reeve',
  url: `${SITE_URL}/`,
  inLanguage: 'en-GB',
  publisher: { '@id': ORG_ID },
  // No SearchAction: there is no on-site search to declare.
};

/** The product. Carries the pricing so engines (and AI answers) can state it accurately. */
export const softwareApplicationSchema = {
  '@type': 'SoftwareApplication',
  '@id': SOFTWARE_ID,
  name: 'reeve',
  url: `${SITE_URL}/`,
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  inLanguage: 'en-GB',
  description: PRODUCT_DESCRIPTION,
  provider: { '@id': ORG_ID },
  publisher: { '@id': ORG_ID },
  areaServed: AREA_SERVED,
  featureList: [
    'AI advisor grounded in current UK employment law and your own documents',
    'A living list of the HR documents your business needs, with a compliance score',
    'HR document templates to start from',
    'On-demand escalation to CIPD-qualified human HR experts',
    'Employment Rights Act 2025 change tracking',
  ],
  // Pricing as an AggregateOffer. Deliberately NO aggregateRating/review — the
  // product is pre-launch and inventing ratings violates Google's policy.
  offers: {
    '@type': 'AggregateOffer',
    priceCurrency: 'GBP',
    lowPrice: '0',
    highPrice: '200',
    offerCount: '4',
    offers: [
      {
        '@type': 'Offer',
        name: 'Free',
        price: '0',
        priceCurrency: 'GBP',
      },
      {
        '@type': 'Offer',
        name: 'Business, up to 4 employees',
        price: '50',
        priceCurrency: 'GBP',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: '50',
          priceCurrency: 'GBP',
          billingDuration: 'P1M',
        },
      },
      {
        '@type': 'Offer',
        name: 'Business, 5 to 9 employees',
        price: '100',
        priceCurrency: 'GBP',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: '100',
          priceCurrency: 'GBP',
          billingDuration: 'P1M',
        },
      },
      {
        '@type': 'Offer',
        name: 'Business, 10 to 50 employees',
        price: '200',
        priceCurrency: 'GBP',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: '200',
          priceCurrency: 'GBP',
          billingDuration: 'P1M',
        },
      },
    ],
  },
};

/** Wrap one or more schema.org nodes in a single JSON-LD document. */
export function jsonLdGraph(nodes: Array<Record<string, unknown>>): Record<string, unknown> {
  return { '@context': 'https://schema.org', '@graph': nodes };
}
